import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Search, ShieldCheck, ShieldOff } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  UniversalEmptyState,
  Button,
  Input,
  Card,
  StatusBadge,
  NotificationCard,
  DateCell,
} from '@afrilink/ui';
import { useWhois, type WhoisResult } from '@/domains/domains-registry';

export default function WhoisPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { result, isLoading, error, lookup } = useWhois();

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!query.trim()) return;
    void lookup(query);
  }

  return (
    <UniversalPage
      title="WHOIS lookup"
      description="Look up registration details for any domain. .co.za uses ZACR; gTLDs use standard RDAP."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Domains', href: '/app/domains' },
        { label: 'WHOIS lookup' },
      ]}
    >
      <Card interactive={false}>
        <form onSubmit={onSubmit} className="p-6 space-y-2">
          <label className="block text-sm font-medium text-navy-primary">Domain</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              placeholder="example.co.za"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
              className="flex-1"
              error={Boolean(error)}
            />
            <Button type="submit" disabled={isLoading || !query.trim()} loading={isLoading}>
              {isLoading ? null : <Search className="h-4 w-4" />}
              {isLoading ? 'Looking up…' : 'Look up'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Tip: enter the bare domain — no <code className="font-sans">https://</code> or path.
          </p>
        </form>
      </Card>

      {error && <NotificationCard tone="danger" title="Lookup failed" body={error.message} />}

      {isLoading && (
        <Card interactive={false}>
          <UniversalLoadingState variant="spinner" caption="Querying registry…" />
        </Card>
      )}

      {result && !isLoading && (
        <WhoisResultCard result={result} onRegister={() => navigate('/app/domains/register')} />
      )}

      {!result && !isLoading && !error && (
        <Card interactive={false}>
          <UniversalEmptyState
            icon={Search}
            title="Enter a domain to begin"
            description="Try one of yours, or any external domain."
          />
        </Card>
      )}
    </UniversalPage>
  );
}

function WhoisResultCard({ result, onRegister }: { result: WhoisResult; onRegister: () => void }) {
  if (result.available) {
    return (
      <Card interactive={false} className="border-success/30">
        <div className="flex items-center gap-3 border-b border-border bg-success/5 px-6 py-4">
          <ShieldCheck className="h-5 w-5 text-success" />
          <div className="flex-1">
            <p className="text-base font-semibold text-navy-primary tabular-nums">{result.domain}</p>
            <p className="text-sm text-success">Available for registration</p>
          </div>
          <Button size="sm" onClick={onRegister}>Register</Button>
        </div>
        <div className="px-6 py-3 text-xs text-muted-foreground inline-flex items-center gap-1">
          Looked up <DateCell value={result.lookedUpAt} format="long" /> · source: {result.source}
        </div>
      </Card>
    );
  }

  return (
    <Card interactive={false}>
      <div className="flex items-center gap-3 border-b border-border bg-warning/5 px-6 py-4">
        <ShieldOff className="h-5 w-5 text-warning" />
        <div className="flex-1">
          <p className="text-base font-semibold text-navy-primary tabular-nums">{result.domain}</p>
          <p className="text-sm text-foreground-soft">Already registered</p>
        </div>
        <StatusBadge tone={result.status?.includes('clientTransferProhibited') ? 'active' : 'draft'}>
          {result.status?.includes('clientTransferProhibited') ? 'Transfer locked' : 'Transferable'}
        </StatusBadge>
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 px-6 py-5 sm:grid-cols-2">
        {result.registrar && <Row label="Registrar" value={result.registrar} />}
        {result.registrant?.fullName && (
          <Row
            label="Registrant"
            value={
              result.registrant.organisation
                ? `${result.registrant.fullName} — ${result.registrant.organisation}`
                : result.registrant.fullName
            }
          />
        )}
        {result.registrant?.email && (
          <Row label="Registrant email" value={result.registrant.email} />
        )}
        {result.registrant?.countryCode && (
          <Row label="Country" value={result.registrant.countryCode} />
        )}
        {result.registeredAt && (
          <Row label="Registered" value={<DateCell value={result.registeredAt} format="long" />} />
        )}
        {result.expiresAt && (
          <Row label="Expires" value={<DateCell value={result.expiresAt} format="long" />} />
        )}
        {result.nameservers && result.nameservers.length > 0 && (
          <Row
            label="Nameservers"
            value={
              <ul className="space-y-0.5 text-xs tabular-nums">
                {result.nameservers.map((ns) => (
                  <li key={ns}>{ns}</li>
                ))}
              </ul>
            }
            spanFull
          />
        )}
      </dl>

      <div className="border-t border-border px-6 py-3 text-xs text-muted-foreground inline-flex items-center gap-1">
        Looked up <DateCell value={result.lookedUpAt} format="long" /> · source: {result.source}
      </div>
    </Card>
  );
}

function Row({
  label,
  value,
  spanFull,
}: {
  label: string;
  value: React.ReactNode;
  spanFull?: boolean;
}) {
  return (
    <div className={spanFull ? 'sm:col-span-2' : undefined}>
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-navy-primary">{value}</dd>
    </div>
  );
}
